<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue';

import { duellyEventEmitter } from '@/services/eventBus';

import ImportNostrKeysModal from './components/ImportNostrKeysModal.vue';
import LoginOverlay from './components/LoginOverlay.vue';
import BottomNavBar from './components/nav-bars/BottomNavBar.vue';
import SideNavBar from './components/nav-bars/SideNavBar.vue';
import TopNavBar from './components/nav-bars/TopNavBar.vue';
import UserService from './services/UserService';
import { useAuthStore } from './store/authStore';
import { useNostrMessageStore } from './store/nostrMessageStore';
import { flushMobileLayout } from './utils/flushMobileLayout';
import TermsOfUseModal from '@/components/TermsOfUseModal.vue';
import { EmptyUserProfile } from '@/types/UserProfile';

const nostrStore = useNostrMessageStore();
(window as any).nostrSync = nostrStore.sync;

const installPromptVisible = ref(false);
const showLoginOverlay = ref(false);
const showImportNostrKeysModal = ref(false);
const remoteNpub = ref('');
const showTermsModal = ref(false);
const termsHtml = ref<string | undefined>(undefined);

const authStore = useAuthStore();
// const nostrMessageStore = useNostrMessageStore();

const maintenanceMode = ref(false); // TRUE = maintenance, FALSE = normal

// Handle install button
const installApp = () => {
    console.log('Install button clicked');
    if (!window.installPrompt) {
        console.log('No deferredPrompt found');
        return;
    }

    window.installPrompt.prompt();
    window.installPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }

        window.installPrompt = null;
        installPromptVisible.value = false;
    });
};

// Listen for install prompt trigger
window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.installPrompt = e;
    installPromptVisible.value = true;
});

function handleScannedImport(data: string) {
    console.log('[QR] handleScannedImport called with:', data);

    try {
        // If the scanned QR is a stringified string (e.g., JSON.stringify(encoded)), parse it first
        if (data.startsWith('"') && data.endsWith('"')) {
            data = JSON.parse(data); // unstringify once
        }

        const parts = data.split('#');
        if (parts.length !== 3) {
            throw new Error('Invalid QR format: expected 3 parts');
        }

        const [privkey, pubkey, npub] = parts;

        if (!privkey || !pubkey || !npub) {
            throw new Error('Missing one or more keys');
        }

        const keyObj = { privkey, pubkey, npub };

        // Save to localStorage and store
        const userId = authStore.getUserProfile().id;
        localStorage.setItem(`nostr_keys.${userId}`, JSON.stringify(keyObj));
        const nostrMessageStore = useNostrMessageStore();
        nostrMessageStore.sync();
        nostrMessageStore.nostrKeys = keyObj;

        const profile = authStore.getUserProfile();
        profile.npub = npub;
        authStore.setProfile(profile);
        UserService.setProfileDirect(profile.id, profile);

        console.log('[QR] Imported Nostr keys successfully:', keyObj);
        showImportNostrKeysModal.value = false;
    } catch (e) {
        console.error('[QR] Failed to import keys from QR:', e);
        alert('Invalid QR Code. Could not parse Nostr key structure.');
    }
}

function handleGenerateNew() {
    console.log('handlerGenrateNew');
    showImportNostrKeysModal.value = false;
    const nostrMessageStore = useNostrMessageStore();
    nostrMessageStore.generateAndStoreNostrKeys(true);
    console.log('Generated new Nostr keys');
}

function updateRouterViewMaxWidth() {
    const sideNavWidth = 300; // or use your real side nav width in px
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const maxWidth = vw - sideNavWidth;
    document.documentElement.style.setProperty('--router-max-width', `${maxWidth}px`);

    // calculate a width that is 9:16 of the vh height form above
    const availableWidth = (vh * 9) / 16;
    console.log('Available width:', availableWidth);
    document.documentElement.style.setProperty('--available-width', `${availableWidth}px`);
}

function setRealVh() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
}

function getPWADisplayMode() {
    if (document.referrer.startsWith('android-app://')) return 'twa';
    if (window.matchMedia('(display-mode: browser)').matches) return 'browser';
    if (window.matchMedia('(display-mode: standalone)').matches) return 'standalone';
    if (window.matchMedia('(display-mode: minimal-ui)').matches) return 'minimal-ui';
    if (window.matchMedia('(display-mode: fullscreen)').matches) return 'fullscreen';
    if (window.matchMedia('(display-mode: window-controls-overlay)').matches)
        return 'window-controls-overlay';

    return 'unknown';
}

function isAndroidChrome() {
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('android') && ua.includes('chrome');
}

function isUrlBarVisible() {
    if (!isAndroidChrome()) return false;

    const viewportHeight = window.innerHeight;
    const screenHeight = window.screen.height;
    return viewportHeight < screenHeight;
}

function updateUrlBarCssVar() {
    const urlBarVisible = isUrlBarVisible();
    console.log('isUrlBarVisible:', urlBarVisible);

    document.documentElement.style.setProperty('--url-bar-visible', urlBarVisible ? '1' : '0');
}

async function handleAcceptTerms() {
    const profile = authStore.getUserProfile();
    // Mark as accepted using the existing flag
    profile.show_tour = false;

    // Optional: stamp acceptance time without backend changes
    const ai = (profile.additional_info ?? {}) as Record<string, any>;
    ai.terms_accepted_at = new Date().toISOString();
    profile.additional_info = ai;

    authStore.setProfile(profile);
    try {
        await UserService.setProfileDirect(profile.id, profile);
    } catch (e) {
        console.warn('[App] Failed to persist Terms acceptance; keeping local state', e);
    }
    showTermsModal.value = false;
}

function handleDeclineTerms() {
    // Soft-logout to anonymous user so they can still browse
    authStore.authTokens = {};
    authStore.setProfile({
        ...EmptyUserProfile,
        is_anonymous: true,
        is_logged_in: false,
    });
    showTermsModal.value = false;
    // (Optional) route to a safe page:
    // router.replace({ name: 'Home' });
}

onMounted(() => {
    console.log('App mounted');

    // hack to cause a layout flush so all the scss and calculated values are updated before the first render
    nextTick(() => {
        setRealVh();
        flushMobileLayout();
        updateRouterViewMaxWidth();
        updateUrlBarCssVar();
    });

    const displayMode = getPWADisplayMode();
    document.documentElement.classList.add(`display-mode-${displayMode}`);

    const isStandalone = displayMode === 'standalone' || displayMode === 'fullscreen';

    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    const isAndroid = /Android/i.test(navigator.userAgent);

    document.documentElement.classList.toggle('platform-android', isAndroid);
    document.documentElement.classList.toggle('platform-ios', /iPhone|iPad|iPod/i.test(navigator.userAgent));

    if (!isStandalone && isMobile) {
        document.documentElement.classList.add('not-installed');
    } else {
        document.documentElement.classList.remove('not-installed');
    }

    window.addEventListener('resize', updateRouterViewMaxWidth);

    duellyEventEmitter.on('show-login-overlay', () => {
        showLoginOverlay.value = true;
    });

    duellyEventEmitter.on('nostr-import-required', (npub) => {
        showImportNostrKeysModal.value = true;
        remoteNpub.value = npub;
    });

    watch(
        () => authStore.userProfile?.id,
        async (userId) => {
            console.log('[UserID] changed:', authStore.userProfile);
            if (!userId) {
                console.log('[UserID] No user ID found');
                showTermsModal.value = false;
                return;
            }

            // existing: ensure nostr keys exist
            const nostrMessageStore = useNostrMessageStore();
            nostrMessageStore.generateAndStoreNostrKeys();

            // Fetch fresh profile (server truth) once user logs in
            try {
                const local = authStore.getUserProfile();
                if (!local.is_anonymous) {
                    const fetched = await UserService.profile(local.id, true);
                    if (fetched) {
                        authStore.setProfile(fetched);
                        // Block until acceptance if needed
                        showTermsModal.value = !!fetched.show_tour;
                    } else {
                        console.warn('[App] Failed to fetch user profile; resetting auth');
                        authStore.authTokens = {};
                        showTermsModal.value = false;
                    }
                } else {
                    showTermsModal.value = false; // anonymous: can browse without accepting
                }
            } catch (e) {
                console.error('[App] profile fetch error:', e);
                showTermsModal.value = false;
            }
        },
        { immediate: true }
    );

    // watch(
    //     () => authStore.userProfile?.id,
    //     (userId) => {
    //         console.log('[UserID] changed:', authStore.userProfile);
    //         if (!userId) {
    //             console.log('[UserID] No user ID found');
    //             return;
    //         }
    //         if (userId) {
    //             const nostrMessageStore = useNostrMessageStore();
    //             nostrMessageStore.generateAndStoreNostrKeys();
    //         }
    //     },
    //     { immediate: true }
    // );
});

onBeforeUnmount(() => {
    duellyEventEmitter.off('show-login-overlay', showLoginOverlayHandler);

    duellyEventEmitter.unregisgter();

    window.removeEventListener('resize', updateRouterViewMaxWidth);
});
</script>

<template>
    <div
        v-if="maintenanceMode"
        style="
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
            flex-direction: column;
        "
    >
        <h1>🔧 Duelly is temporarily undergoing maintenance</h1>
        <p style="margin-top: 12px">We'll be back online shortly!</p>
    </div>

    <v-app v-else>
        <!-- <TopNavBar /> -->
        <router-view v-slot="{ Component }">
            <div class="router-view">
                <component :is="Component" />
            </div>
        </router-view>

        <BottomNavBar @ready="syncBottomNavHeight()" />
        <SideNavBar />
        <LoginOverlay v-model="showLoginOverlay" />
        <!-- <ImportNostrKeysModal
            v-if="showImportNostrKeysModal"
            :remoteNpub="remoteNpub"
            @import-scanned="handleScannedImport"
            @generate-new="handleGenerateNew"
            @cancel="showImportNostrKeysModal = false"
        /> -->

        <TermsOfUseModal
            v-model="showTermsModal"
            :termsHtml="termsHtml"
            @accepted="handleAcceptTerms"
            @declined="handleDeclineTerms"
        />
    </v-app>

    <div v-if="installPromptVisible" class="install-bar" @click="installApp">
        📲 Install Duelly App
    </div>
</template>

<style lang="scss">
@use '@/assets/scss/abstracts/variables' as *;
@use '@/assets/scss/global.scss' as *;

.install-bar {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    background-color: $color-secondary;
    color: white;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    width: 100vw;
    padding: 16px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    z-index: 2000;
    font-weight: bold;
    cursor: pointer;
    text-align: center;
    transition: opacity 0.2s ease;
}
</style>
